'use client'

import React, { useEffect, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { WizardProgress } from '@/components/onboarding/WizardProgress'

import { ProjectOverviewStep } from './steps/ProjectOverviewStep'
import { WorkScopeStep } from './steps/WorkScopeStep'
import { BudgetScheduleStep } from './steps/BudgetScheduleStep'
import { ContactInfoStep } from './steps/ContactInfoStep'
import { ReviewStep } from './steps/ReviewStep'
import { useProjectFormStore } from '@/stores/useProjectFormStore'
import { budgetScheduleSchema, contactInfoSchema, projectFormSchema, projectOverviewSchema, workScopeSchema } from './schemas'

interface FormData {
  projectDomain: string
  neededRoles: string[]
  projectServiceName: string
  shortSummary: string
  projectType?: string
  resultPreset?: string
  collaborationMode?: 'TEAM' | 'INDIVIDUAL'
  functionality: string
  mainCategories: string[]
  subCategories: string[]
  skills: string[]
  workType: string
  period: string
  startDate: string
  cost: string
  contactPhone: string
  contactEmail: string
  referenceService: string
  otherRequirements: string
  designLevel: string
  materials: string[]
  engagementType: string
  weeklyHours: string
}

interface ProjectWizardProps {
  formData: FormData
  selectedFiles: File[]
  onChange: (field: keyof FormData, value: any) => void
  onFileChange: (files: File[]) => void
  onRemoveFile: (index: number) => void
  onSubmit: () => Promise<number | null>
  loading?: boolean
  isOnboarding?: boolean
  clientContactInfo?: { contact_phone: string | null; email: string | null } | null
}

const TOTAL_STEPS = 5


const hasFormData = (formData: FormData) => {
  const hasText = [
    formData.projectDomain,
    formData.projectServiceName,
    formData.shortSummary,
    formData.functionality,
    formData.period,
    formData.cost,
    formData.contactPhone,
    formData.contactEmail,
    formData.referenceService,
    formData.designLevel,
    formData.engagementType,
    formData.weeklyHours,
    formData.otherRequirements,
    formData.startDate,
    formData.workType,
    formData.projectType,
    formData.resultPreset,
  ].some((value) => Boolean(value && String(value).trim().length > 0))

  const hasList = [
    formData.neededRoles,
    formData.mainCategories,
    formData.subCategories,
    formData.skills,
    formData.materials,
  ].some((list) => Array.isArray(list) && list.length > 0)

  return hasText || hasList
}

export const ProjectWizard: React.FC<ProjectWizardProps> = ({
  formData,
  selectedFiles,
  onChange,
  onFileChange,
  onRemoveFile,
  onSubmit,
  loading = false,
  isOnboarding = false,
  clientContactInfo = null,
}) => {
  const router = useRouter()
  const searchParams = useSearchParams()

  // Store에서 currentStep 가져오기
  const { currentStep: storeStep, setCurrentStep: setStoreStep } = useProjectFormStore()
  const [currentStep, setCurrentStep] = useState(storeStep)
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward')
  const [isAnimating, setIsAnimating] = useState(false)
  const [hasPendingChanges, setHasPendingChanges] = useState(false)

  const stepLabels = ['프로젝트 개요', '작업 범위', '예산/일정', '상담 정보', '검토 및 제출']
  const nextButtonLabels = ['다음: 범위 · 예산 입력', '다음: 예산 · 일정 입력', '다음: 연락처 · 자료 첨부', '다음: 검토하기']
  const stepDescriptions = [
    '프로젝트의 핵심 목표를 간단히 정리하세요.',
    '필요한 기능과 카테고리, 스킬을 입력합니다.',
    '예상 기간과 예산, 착수 시점을 선택합니다.',
    '연락처와 참고 자료를 추가합니다.',
    '입력 내용을 최종 확인하고 제출합니다.',
  ]

  // URL query에서 step 읽기
  useEffect(() => {
    const stepParam = searchParams?.get('step')
    const hasSavedData = hasFormData(formData)

    // 저장된 데이터가 없으면 항상 1단계로 강제
    if (!hasSavedData) {
      if (stepParam) {
        const params = new URLSearchParams(searchParams?.toString() || '')
        params.delete('step')
        const query = params.toString()
        router.replace(`/client/projects/new${query ? `?${query}` : ''}`, { scroll: false })
      }
      setCurrentStep(1)
      setStoreStep(1)
      return
    }

    if (stepParam) {
      const step = parseInt(stepParam, 10)
      if (step >= 1 && step <= TOTAL_STEPS) {
        setCurrentStep(step)
        setStoreStep(step)
      }
    } else if (storeStep >= 1 && storeStep <= TOTAL_STEPS) {
      // URL에 step이 없으면 store에서 복원
      setCurrentStep(storeStep)
    }
  }, [searchParams, storeStep, setStoreStep, formData, router])

  // Step 변경 시 URL 업데이트 및 store 저장
  const updateStep = (newStep: number) => {
    const params = new URLSearchParams(searchParams?.toString() || '')
    params.set('step', String(newStep))
    router.push(`/client/projects/new?${params.toString()}`, { scroll: false })
    setCurrentStep(newStep)
    setStoreStep(newStep)
  }

  useEffect(() => {
    setHasPendingChanges(true)
    const timer = setTimeout(() => setHasPendingChanges(false), 500)
    return () => clearTimeout(timer)
  }, [formData, selectedFiles, currentStep])

  const canProceedCurrentStep = () => {
    if (currentStep === 1) {
      return projectOverviewSchema.safeParse({
        projectDomain: formData.projectDomain,
        neededRoles: formData.neededRoles,
        projectServiceName: formData.projectServiceName,
        shortSummary: formData.shortSummary,
        projectType: formData.projectType,
        resultPreset: formData.resultPreset,
        collaborationMode: formData.collaborationMode,
      }).success
    }
    if (currentStep === 2) {
      return workScopeSchema.safeParse({
        functionality: formData.functionality,
        designLevel: formData.designLevel,
        engagementType: formData.engagementType,
        weeklyHours: formData.weeklyHours,
        mainCategories: formData.mainCategories,
        subCategories: formData.subCategories,
        skills: formData.skills,
        workType: formData.workType,
        materials: formData.materials,
        referenceService: formData.referenceService,
        otherRequirements: formData.otherRequirements,
      }).success
    }
    if (currentStep === 3) {
      return budgetScheduleSchema.safeParse({
        period: formData.period,
        startDate: formData.startDate,
        cost: formData.cost,
      }).success
    }
    if (currentStep === 4) {
      return contactInfoSchema.safeParse({
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail,
      }).success
    }
    return true
  }

  const isReviewStepValid = () => projectFormSchema.safeParse(formData).success

  const handleNext = () => {
    if (currentStep >= TOTAL_STEPS) return
    setIsAnimating(true)
    setDirection('forward')
    setTimeout(() => {
      updateStep(currentStep + 1)
      setIsAnimating(false)
    }, 150)
  }

  const handlePrevious = () => {
    if (currentStep <= 1) return
    setIsAnimating(true)
    setDirection('backward')
    setTimeout(() => {
      updateStep(currentStep - 1)
      setIsAnimating(false)
    }, 150)
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ProjectOverviewStep
            formData={{
              projectDomain: formData.projectDomain,
              neededRoles: formData.neededRoles,
              projectServiceName: formData.projectServiceName,
              shortSummary: formData.shortSummary,
              projectType: formData.projectType,
              resultPreset: formData.resultPreset,
              collaborationMode: formData.collaborationMode,
            }}
            onChange={(field, value) => onChange(field as keyof FormData, value)}
            onNext={handleNext}
          />
        )
      case 2:
        return (
          <WorkScopeStep
            formData={{
              functionality: formData.functionality,
              designLevel: formData.designLevel,
              mainCategories: formData.mainCategories,
              subCategories: formData.subCategories,
              skills: formData.skills,
              workType: formData.workType,
              materials: formData.materials,
              referenceService: formData.referenceService,
              engagementType: formData.engagementType,
              weeklyHours: formData.weeklyHours,
              otherRequirements: formData.otherRequirements,
            }}
            onChange={(field, value) => onChange(field as keyof FormData, value)}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        )
      case 3:
        return (
          <BudgetScheduleStep
            formData={{
              period: formData.period,
              startDate: formData.startDate,
              cost: formData.cost,
            }}
            onChange={(field, value) => onChange(field as keyof FormData, value)}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        )
      case 4:
        return (
          <ContactInfoStep
            formData={{
              contactPhone: formData.contactPhone,
              contactEmail: formData.contactEmail,
            }}
            selectedFiles={selectedFiles}
            onChange={(field, value) => onChange(field as keyof FormData, value)}
            onFileChange={onFileChange}
            onRemoveFile={onRemoveFile}
            onNext={handleNext}
            onPrevious={handlePrevious}
            loading={loading}
            clientContactInfo={clientContactInfo}
          />
        )
      case 5:
        return (
          <ReviewStep
            formData={formData}
            selectedFiles={selectedFiles}
            onSubmit={onSubmit}
            onPrevious={handlePrevious}
            loading={loading}
          />
        )
      default:
        return null
    }
  }

  const currentStepTitle = stepLabels[currentStep - 1] ?? ''
  const currentStepDescription = stepDescriptions[currentStep - 1] ?? ''

  return (
    <div className="space-y-6 pb-24">
      {/* 일반 플로우에서만 Progress 표시 (온보딩은 상위 OnboardingProgress 사용) */}
      {!isOnboarding && (
        <div className="space-y-2">
          {/* ✅ 진행률 바 옆에 단계명 노출 */}
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-900">
              {currentStep}/{TOTAL_STEPS} · {currentStepTitle}
            </p>
          </div>

          {/* ✅ 얇은 진행바만 유지 */}
          <WizardProgress currentStep={currentStep} totalSteps={TOTAL_STEPS} />

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {stepLabels.map((label, index) => {
              const step = index + 1
              const isCurrent = step === currentStep
              const isCompleted = step < currentStep

              return (
                <div
                  key={label}
                  className={[
                    'rounded-lg border px-3 py-2 text-xs text-center',
                    isCurrent
                      ? 'border-blue-200 bg-blue-50 text-blue-700'
                      : isCompleted
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-gray-200 bg-white text-gray-500',
                  ].join(' ')}
                >
                  {label}
                </div>
              )
            })}
          </div>

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500">약 2분</p>
            <p className="text-xs text-gray-500 text-right">{currentStepDescription}</p>
          </div>
        </div>
      )}

      {/* Step Content */}
      <div className="relative min-h-[420px] overflow-hidden">
        <div
          key={currentStep}
          className={`transition-all duration-300 ease-in-out ${
            isAnimating
              ? direction === 'forward'
                ? 'opacity-0 translate-x-6'
                : 'opacity-0 -translate-x-6'
              : 'opacity-100 translate-x-0'
          }`}
        >
          {renderStep()}
        </div>
      </div>

      {/* 스티키 하단 바: 모든 단계에서 이전/다음·제출 노출 (페이지 하단 버튼과 중복 제거) */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between gap-3 px-4 py-3">
          <div className="hidden sm:block">
            <p className="text-xs font-medium text-gray-700">
              {hasPendingChanges ? '미저장 변경사항' : '현재 단계 저장됨'}
            </p>
          </div>
          <div className="ml-auto flex w-full items-center justify-end gap-2 sm:w-auto">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentStep === 1 || loading}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              이전
            </button>
            {currentStep < TOTAL_STEPS ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canProceedCurrentStep()}
                className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {nextButtonLabels[currentStep - 1]}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onSubmit()}
                disabled={loading || !isReviewStepValid()}
                className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? '등록 중...' : '프로젝트 등록 완료'}
              </button>
            )}
          </div>
        </div>
        {currentStep === 4 && !canProceedCurrentStep() && (
          <p className="mx-auto w-full max-w-4xl px-4 pb-3 text-right text-xs text-red-500">
            휴대폰 번호 형식(예: 010-1234-5678)과 이메일을 확인해주세요.
          </p>
        )}
        {currentStep === 5 && !isReviewStepValid() && (
          <p className="mx-auto w-full max-w-4xl px-4 pb-3 text-right text-xs text-red-500">
            전화번호 형식을 확인해주세요. 예: 010-1234-5678
          </p>
        )}
      </div>
    </div>
  )
}
